import {
  ArrowRight,
  Bell,
  Bot,
  CalendarClock,
  CheckCircle2,
  Clock,
  Crown,
  Database,
  ExternalLink,
  Eye,
  FileText,
  GitBranch,
  GraduationCap,
  LayoutGrid,
  Lightbulb,
  ListTodo,
  type LucideIcon,
  Mail,
  MailWarning,
  MessageSquare,
  Phone,
  Play,
  Presentation,
  Search,
  Settings,
  Sparkles,
  Target,
  TrendingUp,
  User,
  UserCheck,
  Users,
  Zap,
} from "lucide-react";

// ---------------------------------------------------------------------------
// TabId type
// ---------------------------------------------------------------------------

export type TabId = "hub" | "conversations" | "members" | "resources" | "settings";

// ---------------------------------------------------------------------------
// Utility functions
// ---------------------------------------------------------------------------

export function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function getRoleConfig(role: string) {
  switch (role) {
    case "team_admin":
      return { label: "Admin", color: "text-amber-400", bg: "bg-amber-500/20", icon: Crown };
    case "member":
      return { label: "Member", color: "text-blue-400", bg: "bg-blue-500/20", icon: User };
    case "viewer":
      return { label: "Viewer", color: "text-slate-400", bg: "bg-slate-500/20", icon: Eye };
    default:
      return { label: role, color: "text-slate-400", bg: "bg-slate-500/20", icon: User };
  }
}

// ---------------------------------------------------------------------------
// Icon resolver: maps icon name strings from the API to Lucide components
// ---------------------------------------------------------------------------

export const ICON_MAP: Record<string, LucideIcon> = {
  phone: Phone,
  "file-text": FileText,
  search: Search,
  mail: Mail,
  "trending-up": TrendingUp,
  "list-todo": ListTodo,
  target: Target,
  zap: Zap,
  sparkles: Sparkles,
  bell: Bell,
  "check-circle-2": CheckCircle2,
  "calendar-clock": CalendarClock,
  "mail-warning": MailWarning,
  "user-check": UserCheck,
  "external-link": ExternalLink,
  "git-branch": GitBranch,
  presentation: Presentation,
  "graduation-cap": GraduationCap,
  play: Play,
  "layout-grid": LayoutGrid,
  lightbulb: Lightbulb,
  "arrow-right": ArrowRight,
  bot: Bot,
  users: Users,
  database: Database,
  "message-square": MessageSquare,
  settings: Settings,
  clock: Clock,
};

export function resolveIcon(name?: string): LucideIcon {
  if (!name) return Sparkles;
  return ICON_MAP[name.toLowerCase()] ?? Sparkles;
}

// ---------------------------------------------------------------------------
// Notification type styling
// ---------------------------------------------------------------------------

export function getNotificationStyle(type: string) {
  switch (type) {
    case "follow_up":
      return {
        icon: MailWarning,
        color: "text-brand-primary",
        bg: "bg-brand-primary/10",
        border: "border-brand-primary/20",
      };
    case "task_complete":
      return {
        icon: CheckCircle2,
        color: "text-emerald-400",
        bg: "bg-emerald-500/10",
        border: "border-emerald-500/20",
      };
    case "mention":
      return { icon: UserCheck, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" };
    case "scheduled":
      return { icon: CalendarClock, color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/20" };
    case "alert":
      return { icon: Bell, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20" };
    default:
      return {
        icon: Bell,
        color: "text-content-tertiary",
        bg: "bg-surface-secondary",
        border: "border-border-primary",
      };
  }
}

export function getPriorityDot(priority?: string) {
  switch (priority) {
    case "high":
      return "bg-red-400";
    case "medium":
      return "bg-amber-400";
    case "low":
      return "bg-slate-500";
  }
}

// ---------------------------------------------------------------------------
// Constants for pickers
// ---------------------------------------------------------------------------

export const AVAILABLE_ICONS = Object.keys(ICON_MAP);

// Color presets for the color picker
// `swatch` uses static Tailwind classes so JIT can detect them at build time.
export const COLOR_PRESETS = [
  { label: "Blue", value: "text-blue-400", swatch: "bg-blue-400" },
  { label: "Purple", value: "text-purple-400", swatch: "bg-purple-400" },
  { label: "Emerald", value: "text-emerald-400", swatch: "bg-emerald-400" },
  { label: "Amber", value: "text-amber-400", swatch: "bg-amber-400" },
  { label: "Red", value: "text-red-400", swatch: "bg-red-400" },
  { label: "Cyan", value: "text-cyan-400", swatch: "bg-cyan-400" },
  { label: "Pink", value: "text-pink-400", swatch: "bg-pink-400" },
  { label: "Brand", value: "text-brand-primary", swatch: "bg-brand-primary" },
];

export const GRADIENT_PRESETS = [
  { label: "Blue/Indigo", value: "from-blue-500/20 to-indigo-500/20", swatch: "from-blue-500 to-indigo-500" },
  { label: "Purple/Pink", value: "from-purple-500/20 to-pink-500/20", swatch: "from-purple-500 to-pink-500" },
  { label: "Emerald/Teal", value: "from-emerald-500/20 to-teal-500/20", swatch: "from-emerald-500 to-teal-500" },
  { label: "Amber/Orange", value: "from-amber-500/20 to-orange-500/20", swatch: "from-amber-500 to-orange-500" },
  { label: "Red/Rose", value: "from-red-500/20 to-rose-500/20", swatch: "from-red-500 to-rose-500" },
  {
    label: "Brand",
    value: "from-brand-primary/20 to-brand-secondary/20",
    swatch: "from-brand-primary to-brand-secondary",
  },
];
